import { ArrowRight, BadgeCheck, Factory, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const pillars = [
  {
    icon: BadgeCheck,
    number: "01",
    title: "Company checks",
    text: "Company-check requirements cover the identity of the business representative, company registration and sanctions screening. Public anonymity does not mean anonymity to HIDNstock.",
  },
  {
    icon: ShieldCheck,
    number: "02",
    title: "Products for the European market",
    text: "Sellers must confirm that products meet applicable European product-safety and legal requirements. Products for the European market are not necessarily European-made.",
  },
  {
    icon: Factory,
    number: "03",
    title: "Secondhand from Swedish sorting facilities",
    text: "Secondhand sourcing requirements cover Swedish sorting facilities, traceable sourcing and supplier documentation supporting compliance with applicable Swedish workplace legislation.",
  },
];

export function TradeConfidence() {
  return (
    <section aria-labelledby="trade-confidence-title" className="relative overflow-hidden py-20 md:py-28 bg-foreground text-background border-b-4 border-foreground">
      <span aria-hidden="true" className="deco-text-light absolute -top-4 right-0 text-[22vw] leading-none select-none">
        Trust
      </span>
      <div className="container relative mx-auto px-6 lg:px-8">
        <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-background/60 mb-4">Trust / Supplier accountability</p>
        <h2 id="trade-confidence-title" className="text-3xl md:text-5xl font-bold mb-5">Trade with confidence</h2>
        <p className="text-lg text-background/70 max-w-2xl mb-12">Clear company checks. Clear sourcing requirements. Responsibility that stays with the supplier.</p>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map(({ icon: Icon, number, title, text }) => (
            <article
              key={title}
              className="group border-2 border-background/25 p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-background hover:bg-background hover:text-foreground"
            >
              <div className="flex items-start justify-between mb-8">
                <span className="border-2 border-background/40 p-3 transition-colors duration-300 group-hover:border-foreground group-hover:bg-foreground group-hover:text-background">
                  <Icon aria-hidden="true" className="h-7 w-7" />
                </span>
                <span className="text-sm font-bold tracking-[0.3em] text-background/50 transition-colors duration-300 group-hover:text-foreground/50">
                  {number}
                </span>
              </div>
              <h3 className="text-xl font-bold mb-4">{title}</h3>
              <p className="text-background/70 leading-relaxed transition-colors duration-300 group-hover:text-foreground/70">{text}</p>
            </article>
          ))}
        </div>

        <div className="mt-14 border-2 border-background/25 p-8 md:p-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="max-w-3xl">
            <h3 className="text-xl md:text-2xl font-bold mb-3">Anonymous selling. Accountable suppliers.</h3>
            <p className="text-background/70 leading-relaxed">Supplier identity may be hidden publicly, but not from HIDNstock. Anonymous listings do not remove the supplier’s responsibilities.</p>
          </div>
          <Button asChild variant="outline" className="h-auto min-h-12 py-3 whitespace-normal border-2 border-background bg-transparent text-background hover:bg-background hover:text-foreground uppercase">
            <Link to="/trust">Checks & seller responsibilities<ArrowRight aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1" /></Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
